/**
 * Cally date picker integration using the Popover API.
 * Popups render on the browser's top layer, escaping modal overflow/z-index.
 */
import 'cally';

// Minimal interface for Cally calendar custom elements
interface CallyCalendar extends HTMLElement {
    value?: string;
}

// Track initialized date picker pairs to prevent double-init
const initializedPairs = new Set<string>();

function positionPopover(popup: HTMLElement, input: HTMLElement): void {
    const rect = input.getBoundingClientRect();
    popup.style.inset = 'unset';
    popup.style.position = 'fixed';
    popup.style.left = `${rect.left}px`;
    popup.style.top = `${rect.bottom + 4}px`;
}

/**
 * Initialize a linked start/end date picker pair.
 *
 * Expects popup elements with popover="manual" attribute:
 *   <input id="{id}" .../>
 *   <div id="{id}-popup" popover="manual">
 *     <calendar-date id="{id}-calendar"><calendar-month/></calendar-date>
 *   </div>
 */
export function initDatePickerPair(
    startId: string,
    endId: string,
    allowPastDates: boolean
): void {
    const startInput = document.getElementById(startId) as HTMLInputElement | null;
    const endInput = document.getElementById(endId) as HTMLInputElement | null;
    if (!startInput || !endInput) return;

    // Prevent re-initialization
    const pairKey = `${startId}:${endId}`;
    if (initializedPairs.has(pairKey)) return;
    initializedPairs.add(pairKey);

    const startCalendar = document.getElementById(`${startId}-calendar`) as CallyCalendar | null;
    const endCalendar = document.getElementById(`${endId}-calendar`) as CallyCalendar | null;
    const startPopup = document.getElementById(`${startId}-popup`) as HTMLElement | null;
    const endPopup = document.getElementById(`${endId}-popup`) as HTMLElement | null;

    if (!startCalendar || !endCalendar || !startPopup || !endPopup) return;

    // Set min date (today) if past dates are not allowed
    if (!allowPastDates) {
        const now = new Date();
        const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
        startCalendar.setAttribute('min', today);
        endCalendar.setAttribute('min', today);
    }

    // Restore existing signal values into calendars
    if (startInput.value) {
        startCalendar.setAttribute('value', startInput.value);
        endCalendar.setAttribute('min', startInput.value);
    }
    if (endInput.value) {
        endCalendar.setAttribute('value', endInput.value);
    }

    // Start calendar change handler
    startCalendar.addEventListener('change', (e: Event) => {
        const value = (e.target as HTMLInputElement).value;
        startInput.value = value;
        startInput.dispatchEvent(new Event('input', { bubbles: true }));

        endCalendar.setAttribute('min', value);

        if (!endInput.value || endInput.value < value) {
            endCalendar.setAttribute('value', value);
            endInput.value = value;
            endInput.dispatchEvent(new Event('input', { bubbles: true }));
        }

        startPopup.hidePopover();
    });

    // End calendar change handler
    endCalendar.addEventListener('change', (e: Event) => {
        const value = (e.target as HTMLInputElement).value;
        endInput.value = value;
        endInput.dispatchEvent(new Event('input', { bubbles: true }));
        endPopup.hidePopover();
    });

    // Toggle popover on input click
    startInput.addEventListener('click', () => {
        endPopup.hidePopover();
        positionPopover(startPopup, startInput);
        startPopup.togglePopover();
    });

    endInput.addEventListener('click', () => {
        startPopup.hidePopover();
        positionPopover(endPopup, endInput);
        endPopup.togglePopover();
    });

    // Close on Escape
    document.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
            startPopup.hidePopover();
            endPopup.hidePopover();
        }
    });

    // Close when clicking outside (on the modal backdrop, etc.)
    document.addEventListener('click', (e: MouseEvent) => {
        const target = e.target as Node;
        if (!startInput.contains(target) && !startPopup.contains(target)) {
            startPopup.hidePopover();
        }
        if (!endInput.contains(target) && !endPopup.contains(target)) {
            endPopup.hidePopover();
        }
    });
}
